import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Collapse,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Bolt as BoltIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";

const Sidebar = ({
  lang,
  isMobile,
  selectedMenu,
  openSubmenus,
  mobileOpen,
  handleDrawerToggle,
  handleMenuClick,
  handleSubmenuClick,
  menuItems = [],
  drawerWidth = 260,
}) => {
  const theme = useTheme();
  const { webData } = React.useContext(GlobalContext);

  const appName = webData?.app_name || "Admin";
  const logoUrl = webData?.logo_url ? `/media/${webData.logo_url}` : null;

  // ── All colors come from theme — works in both light & dark ──────────────
  const isActive = (item) =>
    selectedMenu === item.id ||
    item.submenu?.some((s) => s.id === selectedMenu);

  const paperSx = {
    width: drawerWidth,
    border: "none",
    bgcolor: "background.paper",
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: theme.shadows[4],
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Logo ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 62,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          {/* Logo badge */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: logoUrl
                ? "transparent"
                : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden",
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={appName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: "10px",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <Box
              sx={{
                display: logoUrl ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                borderRadius: "10px",
              }}
            >
              <BoltIcon
                sx={{ color: theme.palette.primary.contrastText, fontSize: 19 }}
              />
            </Box>
          </Box>

          {/* Brand text */}
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 14.5,
                color: "text.primary",
                letterSpacing: "0.01em",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 130,
              }}
            >
              {appName}
            </Typography>
            <Typography
              sx={{
                fontSize: 9,
                color: "text.disabled",
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {lang?.appNameDes || "Powered by Codeyon.com"}
            </Typography>
          </Box>
        </Box>

        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* ── Scrollable Nav ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1,
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "action.disabled",
            borderRadius: 4,
          },
        }}
      >
        {/* Group menu items into sections — here we keep it flat */}
        <Box>
          <Typography
            sx={{
              px: 2.5,
              pt: 1.2,
              pb: 0.5,
              fontSize: 10,
              fontWeight: 700,
              color: "text.disabled",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              userSelect: "none",
            }}
          >
            {lang?.navigation || "Navigation"}
          </Typography>

          <List disablePadding sx={{ px: 1 }}>
            {menuItems.map((item) => {
              const active = isActive(item);

              return (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding sx={{ mb: 0.25 }}>
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        "&::before": {
                          content: '""',
                          display: active ? "block" : "none",
                          position: "absolute",
                          left: 0,
                          top: "18%",
                          height: "64%",
                          width: 3,
                          borderRadius: "0 3px 3px 0",
                          bgcolor: "primary.main",
                          zIndex: 1,
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleMenuClick(item.id, !!item.submenu)}
                        sx={{
                          borderRadius: "8px",
                          py: 0.9,
                          px: 1.5,
                          minHeight: 38,
                          bgcolor: active
                            ? alpha(theme.palette.primary.main, 0.1)
                            : "transparent",
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: active
                              ? alpha(theme.palette.primary.main, 0.1)
                              : "action.hover",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: active ? "primary.main" : "text.secondary",
                            transition: "color 0.15s",
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>

                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            color: active ? "text.primary" : "text.secondary",
                            letterSpacing: "0.01em",
                            noWrap: true,
                          }}
                        />

                        {item.submenu &&
                          (openSubmenus[item.id] ? (
                            <ExpandLessIcon
                              sx={{
                                fontSize: 14,
                                color: "text.disabled",
                                ml: 0.5,
                              }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 14,
                                color: "text.disabled",
                                ml: 0.5,
                              }}
                            />
                          ))}
                      </ListItemButton>
                    </Box>
                  </ListItem>

                  {item.submenu && (
                    <Collapse
                      in={!!openSubmenus[item.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List disablePadding sx={{ mb: 0.5 }}>
                        {item.submenu.map((sub) => {
                          const sa = selectedMenu === sub.id;
                          return (
                            <ListItem
                              key={sub.id}
                              disablePadding
                              sx={{ mb: 0.2 }}
                            >
                              <ListItemButton
                                onClick={() => handleSubmenuClick(sub.id)}
                                sx={{
                                  borderRadius: "8px",
                                  py: 0.7,
                                  pl: 5,
                                  pr: 1.5,
                                  minHeight: 34,
                                  bgcolor: sa
                                    ? alpha(theme.palette.primary.main, 0.08)
                                    : "transparent",
                                  "&:hover": {
                                    bgcolor: sa
                                      ? alpha(theme.palette.primary.main, 0.08)
                                      : "action.hover",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: sa ? 6 : 4,
                                    height: sa ? 6 : 4,
                                    borderRadius: "50%",
                                    bgcolor: sa
                                      ? "primary.main"
                                      : "text.disabled",
                                    mr: 1.5,
                                    flexShrink: 0,
                                    transition: "all 0.15s",
                                  }}
                                />
                                <ListItemText
                                  primary={sub.text}
                                  primaryTypographyProps={{
                                    fontSize: 12.5,
                                    fontWeight: sa ? 600 : 400,
                                    color: sa
                                      ? "text.primary"
                                      : "text.secondary",
                                    noWrap: true,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* ── User Row ── */}
      <Box
        sx={{
          px: 2,
          py: 1.4,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: "action.hover",
          flexShrink: 0,
          cursor: "pointer",
          transition: "background-color 0.15s",
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            border: `2px solid ${theme.palette.divider}`,
          }}
        >
          {appName?.charAt(0)?.toUpperCase() || "A"}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {appName}
          </Typography>
          <Typography
            sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.3 }}
          >
            Admin
          </Typography>
        </Box>

        <ExpandMoreIcon
          sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0 }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: paperSx }}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        {drawer}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        PaperProps={{ sx: paperSx }}
        sx={{ display: { xs: "none", md: "block" } }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
